import React, {useMemo} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";

import LineChartTimeSeriesWidget from "./LineChartTimeSeriesWidget";
import {prepareVehicleOverloadTimelineSeriesGrouped} from "../widgets/prepareVehicleOverloadTimelineSeriesGrouped";
import ToggleSection, {useToggleSection} from "../../../widget/ToggleSection";
import ToggleTagGroupPanel from "./ToggleTagGroupPanel";
import {
    excludeSelectedSiteDirectionsActive as excludeSelectedSiteDirections,
    includeSelectedSiteDirectionsActive as includeSelectedSiteDirections,
    setExcludedSiteDirectionsActive as setExcludedSiteDirections,
    setIncludedSiteDirectionsActive as setIncludedSiteDirections,
    toggleExcludedSiteDirectionActive as toggleExcludedSiteDirection,
} from "../../../../state/facade/dataFacade";
import {selectDataMode} from "../../../../state/slice/dataModeSlice";
import {selectSitesAgg} from "../../../../state/facade/statisticsFacade";

function extracted(statsPayload) {
  const dataSourceMode = useSelector(selectDataMode)

  const excludedSiteDirectionNames = useSelector(
      (s) => (dataSourceMode === 'processed' ?
          s.processedMapFiltersSlice.excluded?.siteDirections : s.mapFiltersSlice.excluded?.siteDirections) || [])

  // collect all siteDirectionName values
  const allSiteDirectionItems = useMemo(() => {
    const set = new Set();
    Object.values(statsPayload || {}).forEach((bundle) => {
      (bundle?.dailyStats || []).forEach((row) => {
        if (row?.siteDirectionName) {
          set.add(String(row.siteDirectionName));
        }
      });
    });
    return Array.from(set)
    .map((name) => ({name}))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [statsPayload]);

  const excludedSiteDirectionItems = useMemo(
      () => excludedSiteDirectionNames.map((name) => ({name})),
      [excludedSiteDirectionNames]
  );
  return {
    excludedSiteDirectionNames,
    allSiteDirectionItems,
    excludedSiteDirectionItems
  };
}

const OverloadTrendChart = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isHidden, togglerKey } = useToggleSection(["footer"], ["footer"]);
  const { data: statsPayload = {}, loading: isLoading } = useSelector(selectSitesAgg);

  const {
    excludedSiteDirectionNames,
    allSiteDirectionItems,
    excludedSiteDirectionItems
  } = extracted(statsPayload);

  // filter out excluded siteDirections before building series
  const filteredPayload = useMemo(() => {
    if (!excludedSiteDirectionNames?.length) return statsPayload;
    const out = {};
    Object.entries(statsPayload || {}).forEach(([locationId, siteData]) => {
      const dailyStats = Array.isArray(siteData?.dailyStats)
          ? siteData.dailyStats.filter(
              (row) =>
                  !excludedSiteDirectionNames.includes(
                      String(row?.siteDirectionName || "").trim()
                  )
          )
          : [];
      out[locationId] = { ...siteData, dailyStats };
    });
    return out;
  }, [statsPayload, excludedSiteDirectionNames]);

  const { series, colors, labels } = useMemo(
      () =>
          prepareVehicleOverloadTimelineSeriesGrouped(
              filteredPayload,
              [] // already filtered by siteDirection
          ),
      [filteredPayload]
  );

  const hasData = useMemo(() => {
    if (!Array.isArray(labels) || labels.length === 0) return false;
    if (!Array.isArray(series) || series.length === 0) return false;
    return true || series.some(
        (s) => Array.isArray(s.data) && s.data.some((v) => v != null)
    );
  }, [labels, series]);

  return (
      <>
        {hasData ? (
            <>
             <LineChartTimeSeriesWidget
                id="kpi2-line-overload-trends"
                title="dashboards_view.kpi2.line_overload_trends.title"
                xAxisTitle="dashboards_view.kpi2.line_overload_trends.subtitle"
                yAxisTitle="dashboards_view.kpi2.line_overload_trends.yAxis"
                series={series?.map((s) => ({ ...s, name: t(s.name) }))}
                isLoading={isLoading}
                colors={colors}
                labels={labels}
            />
            </>

        ) : (
            <div className="chart-empty-container has-margin-20 has-min-height-300 is-flex flex-vertical flex-justify-center flex-align-items-center">
              <p>
                <i className="fz fz-empty-set extra-large has-text-grey-lighter"></i>
              </p>
              <p>{t("No data!")}</p>
            </div>
        )}

        <ToggleSection
            sectionKey="footer"
            isHidden={isHidden}
            onToggle={togglerKey}
            title={t("dashboards.tabs.PKI3_content.filter")}
        >
          <ToggleTagGroupPanel
              label={t("dashboards_view.kpi3.site_direction_selection")}
              allItems={allSiteDirectionItems}
              excludedItems={excludedSiteDirectionItems}
              id="name"
              labelKeys={["name"]}
              onToggleItem={(item) => dispatch(toggleExcludedSiteDirection(item.name))}
              onIncludeAllSelectedFromExclude={(items) =>
                  dispatch(includeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onExcludeAllSelectedFromInclude={(items) =>
                  dispatch(excludeSelectedSiteDirections(items.map((i) => i.name)))
              }
              onFlipSelection={() => {
                const all = allSiteDirectionItems.map((i) => i.name);
                const excluded = new Set(excludedSiteDirectionNames);
                const toInclude = all.filter((n) => excluded.has(n));
                const toExclude = all.filter((n) => !excluded.has(n));
                dispatch(setIncludedSiteDirections(toExclude));
                dispatch(setExcludedSiteDirections(toInclude));
              }}
          />
        </ToggleSection>
      </>
  );
};

export default OverloadTrendChart;

